import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { smartFitBounds } from '@/components/time-range-filter'
import { useObjectSet } from '@/features/investigations/hooks/useObjectSet'
import { snippetTimelineItems } from '@/features/source-snippets/projections'
import { type TimelineItem, timelineApi } from '@/services/api/timeline'
import type { ActiveGraphFilter } from '@/types/graph-query'

/** The timeline's query keys: everything of a case starts with `case(caseId)`, so a change that
 *  moves items (a recording's time of recording) invalidates it by that prefix. */
export const pureTimelineKeys = {
  case: (caseId: string) => ['pure-timeline', caseId] as const,
}

export interface UsePureTimelineDataResult {
  isLoading: boolean
  isError: boolean
  error: unknown
  /** Timeline items projected by the backend conventions module — the canonical
   *  source of truth for what to render. Both point and interval items appear
   *  here regardless of whether they originate on a node or an edge.
   *
   *  Scoped to the active investigation's object set on the wire: this hook
   *  passes ``node_ids`` (from ``useObjectSet``) to the API, and the backend
   *  filters at the Cypher layer. There is no client-side filtering — what
   *  the server returns is what the canvas renders.
   *
   *  Fetched in compact mode: ``display_props`` and ``participants.*.geo`` are
   *  omitted to keep the transfer small. The side panel / hover popups fetch
   *  full per-item details on demand from the existing entity / relationship
   *  endpoints, so nothing renders blank — it just enriches after a click.
   *
   *  When no investigation is active (or the active one is empty), the query
   *  is disabled and ``items`` is an empty array — the consumer should render
   *  its empty-state prompt rather than the timeline canvas.
   */
  items: TimelineItem[]
  globalRange: { start: string; end: string } | null
  /** True if any active filter cannot be expressed as an API param (pattern/search). */
  hasBypassedFilter: boolean
}

function splitFilters(filters: ActiveGraphFilter[]): {
  componentId: number | null
  bypassed: number
} {
  let componentId: number | null = null
  let bypassed = 0
  for (const f of filters) {
    if ('componentId' in f) {
      componentId = (f as { componentId: number }).componentId
    } else {
      bypassed += 1
    }
  }
  return { componentId, bypassed }
}

function timestampOf(item: TimelineItem): string {
  return item.kind === 'point' ? item.at : item.start
}

export function usePureTimelineData(
  caseId: string,
  activeFilters: ActiveGraphFilter[]
): UsePureTimelineDataResult {
  const { bypassed } = useMemo(() => splitFilters(activeFilters), [activeFilters])

  // Scoping mirrors Graph and Map: the active investigation's object set is
  // the unit of work users are focused on. We push it to the wire as
  // ``node_ids`` so the backend filters at the Cypher layer, instead of
  // shipping the whole-case projection (~10MB on dense cases) and filtering
  // in the browser. Graph/Map have read this from Zustand since they
  // shipped; the timeline endpoint joined the same model in the
  // ``scope-timeline-to-investigation`` change.
  //
  // Previously this hook also fetched ``/graph/knowledge-graph?limit=5000``
  // for hover/selection enrichment; that call was killed in a prior change
  // (the side panel lazy-fetches details from just an id), so the only
  // remaining fetch is ``/timeline/items``.
  const objectSet = useObjectSet()

  // Sorting the IDs gives the React Query cache a stable, order-independent
  // key — toggling between two equivalent investigations doesn't fragment
  // the cache, and array identity differences between renders don't bust it.
  const sortedNodeIds = useMemo<readonly string[]>(() => {
    if (!objectSet.ids) return []
    return Array.from(objectSet.ids).sort()
  }, [objectSet.ids])

  const isScoped = objectSet.ids !== null && sortedNodeIds.length > 0

  const itemsQuery = useQuery({
    queryKey: [...pureTimelineKeys.case(caseId), 'timeline-items', 'compact', sortedNodeIds],
    queryFn: () =>
      // ``sortedNodeIds`` is non-empty here (gated by ``enabled``), so we
      // always pass it through. The cast keeps the call site a plain array
      // (the API client expects ``string[]``, not ``readonly string[]``).
      timelineApi.getItems(caseId, {
        compact: true,
        node_ids: [...sortedNodeIds],
      }),
    staleTime: 300_000,
    enabled: Boolean(caseId) && isScoped,
  })

  const items = useMemo<TimelineItem[]>(
    () => [...(itemsQuery.data ?? []), ...snippetTimelineItems(objectSet.snippets)],
    [itemsQuery.data, objectSet.snippets]
  )

  // Scrubber domain. We deliberately use the outlier-resistant smart fit
  // (P5–P95 by default) instead of raw min/max so a few far-flung typo
  // dates don't stretch the axis from 2022 to 2038 while the meaningful
  // cluster sits in a few weeks. Falls back to true min/max when there
  // aren't enough events for percentiles to be meaningful.
  const globalRange = useMemo(() => {
    if (items.length === 0) return null
    const stamps: number[] = []
    for (const it of items) {
      const t = new Date(timestampOf(it)).getTime()
      if (Number.isFinite(t)) stamps.push(t)
      if (it.kind === 'interval' && it.end) {
        const e = new Date(it.end).getTime()
        if (Number.isFinite(e)) stamps.push(e)
      }
    }
    if (stamps.length === 0) return null
    const fit = smartFitBounds(stamps)
    if (!fit) return null
    return {
      start: new Date(fit.start).toISOString(),
      end: new Date(fit.end).toISOString(),
    }
  }, [items])

  return {
    isLoading: isScoped && itemsQuery.isLoading,
    isError: itemsQuery.isError,
    error: itemsQuery.error,
    items,
    globalRange,
    hasBypassedFilter: bypassed > 0,
  }
}
